import React, {useMemo, useState} from 'react';
import {Input, Spin,} from 'antd';
import styled from 'styled-components';
import {useQuery} from '@tanstack/react-query';
import {IPosition, IPositions} from "../../../services/types.ts";
import {getSearchWhat} from "../../../services/api.ts";
import {colors} from "../../../util/colors.ts";
import {ModalComponent} from "../ModalComponent.tsx";


interface ProfessionFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (selected: IPosition | null) => void;
    initialSelectedPosition: IPosition | null;
}

export const ProfessionFilterModal: React.FC<ProfessionFilterModalProps> = ({
                                                                                isOpen,
                                                                                onClose,
                                                                                onSave,
                                                                                initialSelectedPosition,
                                                                            }) => {
    const [tempSelected, setTempSelected] = useState<IPosition | null>(initialSelectedPosition);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }
    const {data: positionsData, isLoading} = useQuery<IPositions, Error>({
        queryKey: ['searchWhat'],
        queryFn: getSearchWhat,
        staleTime: Infinity,
        enabled: isOpen,
    });

    const handleSave = () => {
        onSave(tempSelected);
        onClose();
    };

    const handleCancel = () => {
        setTempSelected(initialSelectedPosition);
        onClose();
    };

    const handleSelect = (position: IPosition) => {
        setTempSelected(position);
    }

    const filteredWhatOptions = useMemo(() => {
        if (!searchTerm) {
            return positionsData;
        }
        const lowerSearch = searchTerm.toLowerCase();
        return positionsData?.filter(option =>
            option.label.toLowerCase().includes(lowerSearch) ||
            (option.matching_keywords && option.matching_keywords.toLowerCase().includes(lowerSearch))
        ) ?? [];
    }, [positionsData, searchTerm]);

    const memoizedOptions = useMemo(() => {
        return filteredWhatOptions?.map((option) => (
            <WhatOption
                key={option.id}
                onClick={() => handleSelect(option)}
                isSelectedOpt={tempSelected?.id === option.id}
            >
                {option.label}
            </WhatOption>
        )) ?? [];
    }, [filteredWhatOptions, tempSelected?.id]);

    return (
        <ModalComponent
            title="Was suchen Sie?"
            open={isOpen}
            onClose={handleCancel}
            onSave={handleSave}
            footerValue={
                <div style={{ textAlign: 'left', fontStyle: tempSelected ? 'normal' : 'italic' }}>
                    {tempSelected?.label || "Kein Filter (Alle Positionen)"}
                </div>
            }
        >
            <Input
                size="large"
                placeholder="Berufsbezeichnung oder Tätigkeit suchen..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ marginBottom: 16 }}
                disabled={isLoading}
            />
            <WhatOptionsWrapper>
                {isLoading && <Spin style={{ display: 'block', margin: '20px auto' }} />}
                {!isLoading && memoizedOptions.length === 0 && tempSelected && (
                    <NoResults>Keine Ergebnisse für "{tempSelected.label}"</NoResults>
                )}
                {!isLoading && memoizedOptions.length > 0 && (
                    <OptionsContainer>{memoizedOptions}</OptionsContainer>
                )}
                {!isLoading && !searchTerm && memoizedOptions.length === 0 && (
                    <NoResults>Lade Berufsbezeichnungen...</NoResults>
                )}
            </WhatOptionsWrapper>
        </ModalComponent>
    );
};
// Styled Components
const WhatOptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 70vh;
`;

const OptionsContainer = styled.div`
    overflow-y: auto;
    margin-top: 16px;
    @media (max-width: 767px) {
        height: 40vh;
    }
`;

const NoResults = styled.div`
    padding: 20px;
    text-align: center;
    color: ${colors.text};
`;

export const WhatOption = styled.div<{ isSelectedOpt: boolean; disabled?: boolean }>`
    display: flex;
    padding: 12px 16px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-weight: ${props => props.isSelectedOpt ? 'bold' : 'normal'};
    background: ${props => props.isSelectedOpt ? colors.lightBlue : 'white'};

    &:hover {
        transition: all 0.3s ease;
        background-color: ${colors.lightBlue};
        border-radius: 4px;
    }
`;
